/**
 * Color utilities and constants for Cells & Life
 * Based on PRD Section 7.3 Color Palette
 */

import type { HSLColor } from '@/types';

// UI Color Palette
export const UI_COLORS = {
  // Primary - Scientific Blue
  primary: {
    dark: 0x0A1628,
    main: 0x1A365D,
    light: 0x2D4A6F,
    accent: 0x4299E1,
  },

  // Secondary - Biological Green
  secondary: {
    dark: 0x1C4532,
    main: 0x276749,
    light: 0x38A169,
    accent: 0x68D391,
  },

  // Feedback colors
  feedback: {
    correct: 0x48BB78,
    incorrect: 0xF56565,
    neutral: 0xA0AEC0,
    highlight: 0xF6E05E,
  },

  // UI elements
  ui: {
    background: 0x0A1628,
    surface: 0x1A2332,
    border: 0x2D3748,
    text: 0xE2E8F0,
    textSecondary: 0xA0AEC0,
  },
} as const;

// Cell Visualization Colors
export const CELL_COLORS = {
  membrane: 0x90CDF4,
  cytoplasm: 0xE2E8F0,
  nucleus: 0x805AD5,
  nucleolus: 0x553C9A,
  mitochondria: 0xED8936,
  chloroplast: 0x48BB78,
  ribosome: 0xD53F8C,
  endoplasmicReticulum: 0x63B3ED,
  vacuole: 0x90CDF4,
  cilia: 0x68D391,
  nucleoid: 0xB794F4,
} as const;

// Cell color as HSL for procedural variation
export const CELL_COLORS_HSL: Record<string, HSLColor> = {
  membrane: { h: 205, s: 79, l: 76 },
  cytoplasm: { h: 210, s: 31, l: 91 },
  nucleus: { h: 262, s: 52, l: 65 },
  nucleolus: { h: 262, s: 45, l: 42 },
  mitochondria: { h: 28, s: 83, l: 57 },
  chloroplast: { h: 145, s: 45, l: 51 },
  ribosome: { h: 330, s: 64, l: 55 },
  endoplasmicReticulum: { h: 206, s: 74, l: 65 },
  vacuole: { h: 205, s: 79, l: 76 },
  cilia: { h: 145, s: 55, l: 63 },
  nucleoid: { h: 262, s: 70, l: 78 },
};

/**
 * Convert HSL to hex color number
 */
export function hslToHex(hsl: HSLColor): number {
  const { h, s, l } = hsl;
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0, g = 0, b = 0;

  if (h < 60) {
    r = c; g = x; b = 0;
  } else if (h < 120) {
    r = x; g = c; b = 0;
  } else if (h < 180) {
    r = 0; g = c; b = x;
  } else if (h < 240) {
    r = 0; g = x; b = c;
  } else if (h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  const rHex = Math.round((r + m) * 255);
  const gHex = Math.round((g + m) * 255);
  const bHex = Math.round((b + m) * 255);

  return (rHex << 16) | (gHex << 8) | bHex;
}

/**
 * Convert hex color number to HSL
 */
export function hexToHsl(hex: number): HSLColor {
  const r = ((hex >> 16) & 0xFF) / 255;
  const g = ((hex >> 8) & 0xFF) / 255;
  const b = (hex & 0xFF) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Vary an HSL color by a random amount
 */
export function varyColor(
  baseColor: HSLColor,
  variation: number,
  seed: number
): HSLColor {
  // Simple seeded random
  const random = (s: number): number => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };

  const hVariation = (random(seed) - 0.5) * 2 * variation * 30; // ±30° max
  const sVariation = (random(seed + 1) - 0.5) * 2 * variation * 20; // ±20% max
  const lVariation = (random(seed + 2) - 0.5) * 2 * variation * 15; // ±15% max

  return {
    h: Math.round((baseColor.h + hVariation + 360) % 360),
    s: Math.round(Math.max(0, Math.min(100, baseColor.s + sVariation))),
    l: Math.round(Math.max(0, Math.min(100, baseColor.l + lVariation))),
  };
}

/**
 * Interpolate between two colors
 */
export function lerpColor(color1: number, color2: number, t: number): number {
  const r1 = (color1 >> 16) & 0xFF;
  const g1 = (color1 >> 8) & 0xFF;
  const b1 = color1 & 0xFF;

  const r2 = (color2 >> 16) & 0xFF;
  const g2 = (color2 >> 8) & 0xFF;
  const b2 = color2 & 0xFF;

  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return (r << 16) | (g << 8) | b;
}

/**
 * Adjust color brightness
 */
export function adjustBrightness(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xFF) * factor));
  const g = Math.min(255, Math.round(((color >> 8) & 0xFF) * factor));
  const b = Math.min(255, Math.round((color & 0xFF) * factor));

  return (r << 16) | (g << 8) | b;
}

/**
 * Add alpha to a color (returns Phaser-compatible color object)
 */
export function colorWithAlpha(color: number, alpha: number): { color: number; alpha: number } {
  return { color, alpha };
}
